import { CartErrorEvent } from '@theme/events';

if (!customElements.get('bed-mattress-bundle')) {

  class BedMattressBundle extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      this.bedVariants = this.readJson('[data-bed-variants]');
      this.sizeIndex = Number(this.dataset.bedSizeIndex || 0);
      this.cards = Array.from(this.querySelectorAll('[data-mattress-card]'));
      this.button = this.querySelector('[data-add-bundle]');
      this.defaultButtonText = this.button?.textContent?.trim() || 'Add bed + mattress';
      this.message = this.querySelector('[data-bundle-message]');
      this.summary = this.querySelector('[data-selection-summary]');
      this.detail = this.querySelector('[data-selection-detail]');
      this.isUpdatingVariant = false;
      this.isSubmitting = false;

      this.productForm = document.querySelector(
        `product-form-component[data-product-id="${this.dataset.bedProductId}"]`
      );
      this.variantInput = this.productForm?.querySelector('input[name="id"]');
      this.quantityInput = this.productForm?.querySelector('input[name="quantity"]');
      this.currentBedVariantId = this.variantInput?.value || this.bedVariants[0]?.id;

      this.cards.forEach((card) => {
        card.variants = this.readJson('[data-mattress-variants]', card);
        card.querySelector('input[type="radio"]')?.addEventListener('change', () => this.update(), {
          signal: this.abortController.signal,
        });
      });

      document.addEventListener('variant:update', this.onVariantUpdate, {
        signal: this.abortController.signal,
      });
      document.addEventListener('variant:selected', this.onVariantSelected, {
        signal: this.abortController.signal,
      });
      this.button?.addEventListener('click', this.addBundle, {
        signal: this.abortController.signal,
      });

      this.update();
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    readJson(selector, scope = this) {
      try {
        return JSON.parse(scope.querySelector(selector)?.textContent || '[]');
      } catch (error) {
        console.error('Could not read bundle product data.', error);
        return [];
      }
    }

    normalizeId(value) {
      return String(value || '').split('/').pop();
    }

    normalizeSize(value) {
      const size = String(value || '')
        .toLowerCase()
        .replace(/[’']/g, '')
        .replace(/\s+/g, ' ')
        .trim();

      if (size.includes('6ft') || size.includes('super king')) return 'super-king';
      if (size.includes('5ft') || (size.includes('king') && !size.includes('super'))) return 'king';
      if (size.includes('small double') || size.startsWith('4ft ')) return 'small-double';
      if (size.includes('4ft6') || size.includes('46') || size === 'double' || size.endsWith(' double')) return 'double';
      if (size.includes('3ft') || size.includes('single')) return 'single';
      return size.replace(/[^a-z0-9]+/g, '-');
    }

    formatSize(value) {
      const normalized = this.normalizeSize(value);
      const labels = {
        single: '3ft Single',
        'small-double': '4ft Small Double',
        double: '4ft6 Double',
        king: '5ft King',
        'super-king': '6ft Super King',
      };
      return labels[normalized] || value || 'Selected size';
    }

    formatMoney(pence) {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: this.dataset.currency || 'GBP',
        minimumFractionDigits: Number(pence) % 100 === 0 ? 0 : 2,
      }).format(Number(pence) / 100);
    }

    get currentBedVariant() {
      const id = this.normalizeId(this.currentBedVariantId);
      return this.bedVariants.find((variant) => this.normalizeId(variant.id) === id);
    }

    get currentBedSize() {
      return this.currentBedVariant?.options?.[this.sizeIndex] || '';
    }

    get selectedCard() {
      return this.cards.find((card) => card.querySelector('input[type="radio"]')?.checked);
    }

    getMatchingMattressVariant(card) {
      const wantedSize = this.normalizeSize(this.currentBedSize);
      return card?.variants?.find(
        (variant) => variant.available && this.normalizeSize(variant.title) === wantedSize
      );
    }

    onVariantSelected = (event) => {
      const sourceSection = event.target instanceof Element ? event.target.closest('.shopify-section') : null;
      const relatedProductForm = sourceSection?.querySelector(
        `product-form-component[data-product-id="${this.dataset.bedProductId}"]`
      );
      if (!relatedProductForm) return;

      this.isUpdatingVariant = true;
      if (this.button) {
        this.button.disabled = true;
        this.button.textContent = 'Updating…';
      }
    };

    onVariantUpdate = (event) => {
      const eventProductId = this.normalizeId(event.detail?.data?.newProduct?.id || event.detail?.data?.productId);
      const bedProductId = this.normalizeId(this.dataset.bedProductId);
      if (eventProductId !== bedProductId) return;

      this.currentBedVariantId = event.detail?.resource?.id || '';
      this.isUpdatingVariant = false;
      if (this.button) this.button.textContent = this.defaultButtonText;
      this.update();
    };

    update() {
      const bedVariant = this.currentBedVariant;
      const sizeLabel = this.formatSize(this.currentBedSize);
      let firstAvailableCard;

      this.cards.forEach((card) => {
        const radio = card.querySelector('input[type="radio"]');
        const matchedVariant = this.getMatchingMattressVariant(card);
        const size = card.querySelector('[data-size-label]');
        const regular = card.querySelector('[data-regular-price]');
        const offer = card.querySelector('[data-offer-price]');
        const saving = card.querySelector('[data-saving-label]');

        card.matchedVariant = matchedVariant;
        card.classList.toggle('is-unavailable', !matchedVariant);
        if (radio) radio.disabled = !matchedVariant;

        if (matchedVariant) {
          firstAvailableCard ||= card;
          const offerPrice = Math.round(Number(matchedVariant.price) * 0.75);
          if (size) size.textContent = sizeLabel;
          if (regular) regular.textContent = this.formatMoney(matchedVariant.price);
          if (offer) offer.textContent = this.formatMoney(offerPrice);
          if (saving) saving.textContent = `Save ${this.formatMoney(Number(matchedVariant.price) - offerPrice)}`;
        } else {
          if (size) size.textContent = `Unavailable in ${sizeLabel}`;
          if (regular) regular.textContent = '';
          if (offer) offer.textContent = 'Unavailable';
          if (saving) saving.textContent = 'Choose another mattress';
        }
      });

      if (!this.selectedCard?.matchedVariant && firstAvailableCard) {
        firstAvailableCard.querySelector('input[type="radio"]').checked = true;
      }

      const selected = this.selectedCard;
      const mattressVariant = selected?.matchedVariant;
      const mattressTitle = selected?.querySelector('.complete-your-bed__title')?.textContent?.trim();
      const bedAvailable = Boolean(bedVariant?.available);
      const canAdd = Boolean(bedAvailable && mattressVariant);

      if (this.button && !this.isSubmitting && !this.isUpdatingVariant) {
        this.button.disabled = !canAdd;
        this.button.textContent = this.defaultButtonText;
      }

      if (!bedAvailable) {
        if (this.summary) this.summary.textContent = 'This bed combination is unavailable';
        if (this.detail) this.detail.textContent = 'Choose another bed option to continue.';
      } else if (mattressVariant) {
        const offerPrice = Math.round(Number(mattressVariant.price) * 0.75);
        if (this.summary) this.summary.textContent = `${mattressTitle} — ${sizeLabel}`;
        if (this.detail) {
          this.detail.textContent = `${this.formatMoney(offerPrice)} after the automatic 25% mattress discount.`;
        }
      } else {
        if (this.summary) this.summary.textContent = 'No matching mattress is available';
        if (this.detail) this.detail.textContent = `Try another mattress or bed size.`;
      }

      if (this.message) {
        this.message.textContent = '';
        this.message.classList.remove('is-error');
      }
    }

    addBundle = async () => {
      const bedVariant = this.currentBedVariant;
      const mattressVariant = this.selectedCard?.matchedVariant;
      if (!bedVariant?.available || !mattressVariant || !this.button) return;
      const quantity = Math.max(1, Number(this.quantityInput?.value) || 1);

      const originalText = this.button.textContent;
      this.isSubmitting = true;
      this.button.disabled = true;
      this.button.textContent = 'Adding bed and mattress…';
      if (this.message) {
        this.message.textContent = '';
        this.message.classList.remove('is-error');
      }

      const body = {
        items: [
          { id: Number(this.normalizeId(bedVariant.id)), quantity },
          { id: Number(this.normalizeId(mattressVariant.id)), quantity },
        ],
      };

      try {
        const response = await fetch(this.dataset.cartAddUrl || '/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        });

        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'The bundle could not be added.');

        this.button.textContent = 'Bed + mattress added';
        if (this.message) this.message.textContent = 'Your bed and matching mattress are now in the basket.';
        window.setTimeout(() => {
          this.isSubmitting = false;
          this.button.textContent = originalText;
          this.button.disabled = false;
          window.location.assign(this.dataset.cartUrl || '/cart');
        }, 450);
      } catch (error) {
        if (this.message) {
          this.message.textContent = error.message || 'The bundle could not be added. Please try again.';
          this.message.classList.add('is-error');
        }
        this.dispatchEvent(
          new CartErrorEvent(this.id, error.message || 'Unable to add this bundle.', error.message, null)
        );
        this.isSubmitting = false;
        this.button.disabled = false;
        this.button.textContent = originalText;
      }
    };
  }

  customElements.define('bed-mattress-bundle', BedMattressBundle);
}
